import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { Email as EmailIcon } from "@mui/icons-material";
import SnackBar from "../common/SnackBar";
import Navbar from "../common/Navbar";
import Newfooter from "../common/NewFooter";
import ForgotPasswordIllustration from "../../Assets/forgotpassword.png";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
    onClose: () => setSnackbar({ ...snackbar, open: false }),
  });

  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || "Failed to send reset email");
      }

      setSnackbar({
        open: true,
        severity: "success",
        message:
          "Temporary password sent to your email. Please check your inbox.",
        onClose: () => {
          setSnackbar({ ...snackbar, open: false });
          navigate("/");
        },
      });
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: error.message || "An error occurred. Please try again.",
        onClose: () => setSnackbar({ ...snackbar, open: false }),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box
        sx={{
          mt: 7,
          minHeight: "calc(100vh - 128px)",
          backgroundColor: "#f1f5f9",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            borderRadius: "16px",
            maxWidth: "900px",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Grid container>
            {/* Illustration Side */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                backgroundColor: "#eef3f8",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 3,
              }}
            >
              <Box
                component="img"
                src={ForgotPasswordIllustration}
                alt="Forgot Password Illustration"
                sx={{
                  width: "100%",
                  maxWidth: { xs: "180px", sm: "220px", md: "280px" },
                  height: { xs: "auto", sm: "auto", md: "auto" },
                }}
              />
            </Grid>

            {/* Form Side */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                padding: 4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Forgot <span style={{ color: "#3f8c8d" }}>Password?</span>
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Enter your email address and we'll send you a temporary password
              </Typography>

              <form onSubmit={onSubmit}>
                <TextField
                  fullWidth
                  required
                  label="Enter Email Address"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3, borderRadius: "12px" }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    backgroundColor: "#3f8c8d",
                    textTransform: "none",
                    borderRadius: "24px",
                    boxShadow: "0 4px 12px rgba(63, 140, 141, 0.3)",
                    "&:hover": {
                      backgroundColor: "#357c7d",
                    },
                    height: "44px",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Next"
                  )}
                </Button>
              </form>

              <Typography
                variant="body2"
                onClick={() => navigate("/")}
                sx={{
                  mt: 2,
                  textAlign: "center",
                  color: "#3f8c8d",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Back To Login
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      <SnackBar snackbar={snackbar} />
      <Newfooter />
    </>
  );
};

export default ForgotPassword;
